import lodash from 'lodash';
import i18next, { BackendModule, i18n as I18n, NewableModule, ResourceKey } from 'i18next';
import ChainedBackend, { ChainedBackendOptions } from 'i18next-chained-backend';
import HttpBackend from 'i18next-http-backend';
import i18nextResourcesToBackend from 'i18next-resources-to-backend';
import i18nResources from '@solace-labs/backstage-plugin-ep-devp-common/i18n-resources';

type Backend<T = object> = BackendModule<T> | NewableModule<BackendModule<T>>;

/** @alpha */
export type EpDevPI18nBackend<TOptions = object> = {
  namespace: string;
  backend: Backend<TOptions>;
  backendOptions?: TOptions;
}

/** @alpha */
export type EpDevPI18nPlugin = Parameters<typeof i18next.use>[0];

type ResourcesToBackendOptions = {
  namespace: string;
  resources: (language: string) => ResourceKey | undefined | Promise<ResourceKey | undefined>;
}

/**
 * Creates a backend module to lazy load resources.
 */
const resourcesToBackend = (options: ResourcesToBackendOptions): EpDevPI18nBackend => {

  const backend = i18nextResourcesToBackend((language: string, ns: string) => {
    if (ns === options.namespace) {
      return options.resources(language);
    }
    return undefined;
  });

  return {
    namespace: options.namespace,
    backend,
  };
}

type HttpBackendOptions = {
  namespace: string;
  loadPath: (language: string) => string | Promise<string>;
  resourcePath?: string;
}

/**
 * Creates a backend module to load resources via HTTP fetch.
 */
const createHttpBackend = (options: HttpBackendOptions): EpDevPI18nBackend => {

  const backendOptions = {
    loadPath: (languages: string[], namespaces: string[]) => {
      if (namespaces.length === 1 && namespaces[0] === options.namespace) {
        return options.loadPath(languages[0]);
      }
      return false;
    },
    parse: (data: string) => {
      const resourceBundle = JSON.parse(data);
      if (options.resourcePath) {
        return lodash.get(resourceBundle, options.resourcePath, {});
      }
      return resourceBundle;
    }
  }

  return {
    namespace: options.namespace,
    backend: HttpBackend,
    backendOptions,
  }
}

type CreateInstanceOptions = {
  backends: EpDevPI18nBackend[];
  debug?: boolean;
  plugins?: EpDevPI18nPlugin[];
}

/**
 * Creates a custom instance of `i18next`.
 */
const createInstance = (options: CreateInstanceOptions): I18n => {

  const i18n = i18next.createInstance();
  if (options.plugins) {
    options.plugins.forEach(plugin => i18n.use(plugin));
  }

  const namespaces = options.backends.map(backend => backend.namespace);

  const backends: ChainedBackendOptions['backends'] = [];
  const backendOptions: ChainedBackendOptions['backendOptions'] = [];

  const commonResourcesBackend = resourcesToBackend({
    namespace: 'common',
    resources: language => i18nResources.getResourceBundle(language),
  });

  backends.push(commonResourcesBackend.backend);
  backendOptions.push(commonResourcesBackend.backend);

  options.backends.forEach(backend => {
    backends.push(backend.backend);
    backendOptions.push(backend.backendOptions);
  });

  backends.push(i18nextResourcesToBackend(() => ({})));
  backendOptions.push({});

  i18n.use(ChainedBackend).init<ChainedBackendOptions>({
    debug: options.debug,
    fallbackLng: 'en',
    load: 'languageOnly',
    ns: [...namespaces, 'common'],
    fallbackNS: namespaces.slice(1),
    interpolation: {
      escapeValue: false,
      skipOnVariables: false,
    },
    returnNull: false,
    backend: {
      backends,
      backendOptions,
    },
  });

  i18n.services.formatter?.add('capitalize', value => {
    if (typeof value === 'string') {
      return value.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
    }
    return value;
  });

  i18n.services.formatter?.add('lowercase', value => {
    if (typeof value === 'string') {
      return value.toLowerCase();
    }
    return value;
  });

  i18n.services.formatter?.add('uppercase', value => {
    if (typeof value === 'string') {
      return value.toUpperCase();
    }
    return value;
  });

  return i18n;
}

/**
 * Creates a copy of a resource bundle with all nested references replaced with their actual translations.
 * 
 * @example
 * 
 * Resource bundle with nested references:
 * ```json
 * {
 *   "key1": "Key1",
 *   "textWithKey1": "text with $t(key1)"
 * }
 * ``` 
 * Resource bundle with nested references replaced:
 * ```json
 * {
 *   "key1": "Key1",
 *   "textWithKey1": "text with Key1"
 * }
 * ```
 */
const createTemplateForResourceBundle = async (resourceBundle: ResourceKey): Promise<ResourceKey> => {

  if (typeof resourceBundle !== 'object') return resourceBundle;

  const i18n = i18next.createInstance();

  const normalize = (resources: object, parentPath?: string) => {
    const result: ResourceKey = {};
    Object.entries(resources).forEach(([key, value]) => {
      const path = parentPath ? `${parentPath}.${key}` : key;
      if (key.startsWith('$')) {
        // internal key, omit from result
      } else if (typeof value === 'object') {
        result[key] = normalize(value, path);
      } else {
        result[key] = i18n.t(path);
      }
    });
    return result;
  }

  return new Promise((resolve) => {

    i18n.init({
      resources: {
        'en': {
          'common': i18nResources.getResourceBundle('en') ?? {},
          'translation': resourceBundle,
        },
      },
      lng: 'en',
      ns: ['translation', 'common'],
      interpolation: {
        escapeValue: false,
        skipOnVariables: true,
      },
    });

    i18n.services.formatter?.add('capitalize', value => {
      if (typeof value === 'string') {
        return value.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
      }
      return value;
    });

    i18n.services.formatter?.add('lowercase', value => {
      if (typeof value === 'string') {
        return value.toLowerCase();
      }
      return value;
    });

    i18n.services.formatter?.add('uppercase', value => {
      if (typeof value === 'string') {
        return value.toUpperCase();
      }
      return value;
    });

    if (i18n.isInitialized) {
      resolve(normalize(resourceBundle));
    } else {
      i18n.on('initialized', () => resolve(normalize(resourceBundle)));
    }
  });
}

/** @alpha */
export const epDevPI18nHelper = {
  createInstance,
  createHttpBackend,
  createTemplateForResourceBundle,
  resourcesToBackend,
}

export const epDevPi18nCreateInstance = createInstance;
export const epDevPi18nCreateHttpBackend = createHttpBackend;
export const epDevPi18nCreateTemplateForResourceBundle = createTemplateForResourceBundle;
export const epDevPi18nResourcesToBackend = resourcesToBackend;
